import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { translator } from './format';
import type { Translate } from './format';
import { en } from './en';
import type { Messages } from './en';
import { es } from './es';
import { DEFAULT_UI_LOCALE, UI_LOCALES, browserLocales, narrowUiLocale } from './locale';
import type { UiLocale } from './locale';

/**
 * The catalogs, the one function a component uses, and the rule that decides
 * which language they are in.
 *
 * ## The rule, stated once
 *
 * The operator console is **cross-tenant by definition** (spec §6.3), so there
 * is no tenant whose default locale could possibly apply: the seat belongs to
 * whoever is sitting in it. The language therefore comes from **the operator's
 * own browser** (`navigator.languages`), narrowed to the catalog set, and
 * resolved **once at module load** — the same rule the staff PWA follows, for a
 * different reason arriving at the same place. `locale.ts` carries the long
 * form of the argument.
 *
 * ## Why the context's *default value* is the device answer
 *
 * Because then there is nothing to wire up. `MessagesProvider` exists so a test
 * can render one screen in Spanish, and so a future preference control has
 * somewhere to live — but a component that is never wrapped still gets a real
 * catalog rather than a crash or an English fallback nobody chose. That is the
 * opposite of `useConsole`, which throws outside its provider on purpose: an API
 * client has no sane default and a language does.
 *
 * Both catalogs are in the bundle. At two languages and a couple of kilobytes
 * gzipped, code-splitting them would buy a round trip on the surface that is
 * least sensitive to round trips.
 */

const CATALOGS: Record<UiLocale, Messages> = { en, es };

export type { Messages, UiLocale };
export { DEFAULT_UI_LOCALE, UI_LOCALES, browserLocales, narrowUiLocale };

/** The type a component prop or a context field uses to carry `t` around. */
export type T = Translate<Messages>;

export function catalogFor(locale: UiLocale): Messages {
  return CATALOGS[locale];
}

/** A bound `t` for one language. */
export function translatorFor(locale: UiLocale): T {
  return translator(CATALOGS[locale]);
}

/**
 * The operator's language, decided once.
 *
 * `navigator.languages` cannot change without a page load, so re-reading it per
 * render would be a hook that always answered the same thing while implying it
 * might not.
 */
export const DEVICE_LOCALE: UiLocale = narrowUiLocale(...browserLocales());

interface Bound {
  locale: UiLocale;
  t: T;
}

const DEVICE: Bound = { locale: DEVICE_LOCALE, t: translatorFor(DEVICE_LOCALE) };

const MessagesContext = createContext<Bound>(DEVICE);

export function MessagesProvider({
  locale,
  children,
}: {
  locale: UiLocale;
  children: ReactNode;
}): ReactNode {
  const value = useMemo<Bound>(() => ({ locale, t: translatorFor(locale) }), [locale]);
  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
}

/** The console's own words. */
export function useT(): T {
  return useContext(MessagesContext).t;
}

/**
 * The active locale, for `Intl` — so a formatted weekday, price or clock reading
 * is in the same language as the sentence beside it.
 */
export function useLocale(): UiLocale {
  return useContext(MessagesContext).locale;
}
